/**
 * Frontend mirror of the backend urlName slug policy
 * (DasFinSuiteBackend `src/das_fin_suite_backend/utils/url_name.py`).
 *
 * `urlName` is a URL-safe slug derived from an application's name and used to
 * build the human-readable embedded-app URL (e.g. `/app/prophecy-arc`). The rules
 * here MUST stay in sync with the Python source of truth, which is the
 * authoritative validator at write time.
 *
 * Slug rules (identical to the backend):
 *   1. lowercase
 *   2. DROP these characters entirely:  &  +  *
 *   3. convert hyphen '-'  ->  underscore '_'   (existing '_' is preserved)
 *   4. convert every other run of non-[a-z0-9_] characters  ->  a single dash '-'
 *   5. collapse any run of repeated separators ('-' or '_', including mixed) to a
 *      single dash; strip leading/trailing '-' and '_'
 */

const DROP_CHARS = /[&+*]/g;
const HYPHEN = /\s*-\s*/g;
const OTHER_SEP = /[^a-z0-9_]+/g;
// Collapse any run of 2+ separators (--, __, -_, _-) to one dash so the result
// always satisfies isValidUrlNameFormat (which forbids doubled separators).
const MULTISEP = /[-_]{2,}/g;

/**
 * Convert an application name into its URL-safe slug. Returns '' when the name
 * yields no slug-able characters (caller decides how to handle that — the form
 * leaves the field empty and the backend keeps the id-based URL).
 */
export const generateUrlName = (applicationName: string | null | undefined): string => {
  let s = (applicationName ?? '').trim().toLowerCase();
  s = s.replace(DROP_CHARS, '');
  s = s.replace(HYPHEN, '_');
  s = s.replace(OTHER_SEP, '-');
  s = s.replace(MULTISEP, '-');
  return s.replace(/^[-_]+|[-_]+$/g, '');
};

/**
 * A urlName that a human (admin) is allowed to save: lowercase alphanumeric
 * segments separated by a single '-' or '_', with no leading/trailing/doubled
 * separator. Every value `generateUrlName` produces satisfies this. Keep in
 * sync with the backend `_VALID_URL_NAME` pattern.
 */
const VALID_URL_NAME = /^[a-z0-9]+([-_][a-z0-9]+)*$/;

export const isValidUrlNameFormat = (value: string | null | undefined): boolean => typeof value === 'string' && VALID_URL_NAME.test(value);

/** True when `value` is a well-formed urlName safe to persist. */
export const isValidUrlName = (value: string | null | undefined): boolean => isValidUrlNameFormat(value);

/** Minimal shape needed to build an app's in-portal path. */
export interface EmbeddableAppRef {
  id: string;
  urlName?: string | null;
}

/**
 * Canonical in-portal path for an embedded application: the human-readable
 * `/app/{urlName}` when a slug exists, otherwise the legacy `/all-apps/{id}`
 * (which the router redirects to the slug when one becomes available). The
 * `/app/` prefix namespaces embedded apps so a slug can never collide with a
 * top-level route.
 */
export const buildEmbeddedAppPath = (app: EmbeddableAppRef): string => (app.urlName ? `/app/${app.urlName}` : `/all-apps/${app.id}`);
